import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Grid,
  Box,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate } from "react-router-dom";
import Pedido, {
  estadoInicialFiltroPesquisarPedido,
  FiltroPesquisarPedido,
} from "../Shared/Interfaces/Pedido";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { PedidoService } from "../../Services/PedidoService";
import React, { useContext, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import ImprimirPedido from "./ImprimirPedido";
import {
  formatDateToDDMMYYYY,
  formatToBRL,
} from "../../Utils/utils";
import { AppProvider } from "../../ContextApi/AppProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import DefaultLoader from "../Shared/DefaultLoader";
import colors from "../../theme/colors";

export default function ConsultarPedidos() {
  const navigate = useNavigate();
  const contextApi = useContext(AppProvider);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dataInicial, setDataInicial] = useState<Dayjs>(dayjs().subtract(3, 'day'));
  const [dataFinal, setDataFinal] = useState<Dayjs>(dayjs());
  const [filtroPesquisarPedido, setFiltroPesquisarPedido] =
    React.useState<FiltroPesquisarPedido>(estadoInicialFiltroPesquisarPedido);
  const [loading, setLoading] = useState(false);
  
  const handleInputPesquisarPedido = (
    fieldName: keyof FiltroPesquisarPedido,
    value: string | number
  ) => {
    if (fieldName == "idPedido" && value == "") {
      value = 0;
    }
    setFiltroPesquisarPedido((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pedidoService = new PedidoService();
  const [pedidos, setPedidos] = React.useState<Pedido[]>([]);
  
  const handlePrint = (pedido: Pedido) => {
    openPDFInNewTab(pedido);
  };

  const handleDeleteItem = (pedido: Pedido) => {
    pedidoService
      .deleteItem(`/api/Pedidos/Deletar/${pedido.id}`)
      .then((response) => {
        if (response.status == 204) {
          contextApi.showMessage(
            "Pedido excluído com sucesso!",
            true,
            "success"
          );
          const filtroPesquisa: FiltroPesquisarPedido = {
            idPedido: 0,
            nomeCliente: '',
            dataInicial: dataInicial != null ? dataInicial.toISOString() : null,
            dataFinal: dataFinal != null ? dataFinal.toISOString() : null,
          };
          PesquisarPor(filtroPesquisarPedido);
        } else {
          contextApi.showMessage(
            "Erro ao excluir o Pedido, tente novamente!",
            true,
            "error"
          );
        }
      })
      .catch((error) => {
        contextApi.showMessage(error.response.data, true, "error");
      });
  };

  const openPDFInNewTab = async (pedido: Pedido) => {
    const blob = await pdf(
      <ImprimirPedido data={pedido} numeroPedido={`#000${pedido.id}`} />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const PesquisarPor = (filtroPesquisarPedido: FiltroPesquisarPedido) => {
    setLoading(true);

    const filtroPesquisa: FiltroPesquisarPedido = {
       idPedido: filtroPesquisarPedido.idPedido,
       nomeCliente: filtroPesquisarPedido.nomeCliente,
       dataInicial: dataInicial != null ? dataInicial.toISOString() : null,
       dataFinal: dataFinal != null ? dataFinal.toISOString() : null,
     };

    pedidoService
      .buscarPor(filtroPesquisa)
      .then((response) => {
        if (response.status === 200) {
          setPedidos(response.data); // Updates the pedidos state with the response data
          setFiltroPesquisarPedido(estadoInicialFiltroPesquisarPedido);
          setLoading(false);
        }
      })
      .catch((error) => {
        const errorMessage = error.response?.data || "An error occurred"; // Handle potential undefined error responses
        contextApi.showMessage(errorMessage, true, "error");
        setLoading(false);
      });
  };

  const handleEditPedido = (row: Pedido) => {
    navigate(`/app/gerarpedidos/${row.id}`);
  };

  React.useEffect(() => {
    const filtroPesquisa: FiltroPesquisarPedido = {
      idPedido: 0,
      nomeCliente: '',
      dataInicial: dataInicial != null ? dataInicial.toISOString() : null,
      dataFinal: dataFinal != null ? dataFinal.toISOString() : null,
    };
    PesquisarPor(filtroPesquisarPedido);
  }, []); // Empty array ensures this runs only once when the component mounts

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Linha 1 - Cabeçalho e Descrição */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, bgcolor: '#f8f9fa' }}>
              <h4 style={{ margin: '0 0 0 0', color: colors.bluePrimary, textAlign: 'left' }}>
                🔍 Consultar Pedidos
              </h4>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Pesquisa e visualização de pedidos existentes
              </Typography>
            </Paper>
          </Grid>

          {/* Linha 2 - Filtros de Pesquisa */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>🔍 Filtros de Pesquisa</h4>
              
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <Grid container spacing={2} alignItems="center">
                  {/* Filtro ID Pedido */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      name="id"
                      label="ID Pedido"
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={filtroPesquisarPedido.idPedido}
                      onChange={(e) =>
                        handleInputPesquisarPedido("idPedido", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Filtro Nome Cliente */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      name="nome"
                      label="Cliente"
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={filtroPesquisarPedido.nomeCliente}
                      onChange={(e) =>
                        handleInputPesquisarPedido("nomeCliente", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Filtro Data Inicial */}
                  <Grid item xs={12} sm={6} md={2}>
                      <DatePicker
                        label="Data Inicial"
                        value={dataInicial}
                        format="DD/MM/YYYY"
                        onChange={(newValue) =>
                          setDataInicial(newValue ?? dataInicial)
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true
                          }
                        }}
                      />
                  </Grid>

                  {/* Filtro Data Final */}
                  <Grid item xs={12} sm={6} md={2}>
                      <DatePicker
                        label="Data Final"
                        value={dataFinal}
                        format="DD/MM/YYYY"
                        onChange={(newValue) =>
                          setDataFinal(newValue ?? dataFinal)
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true
                          }
                        }}
                      />
                  </Grid>

                  {/* Botão Pesquisar */}
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{ height: '40px' }}
                      onClick={() => PesquisarPor(filtroPesquisarPedido)}
                    >
                      {loading ? (
                        <DefaultLoader
                          height="2.5em"
                          width="2.5em"
                          colors={["#1876D2", "#1876D2", "#1876D2", "#1876D2", "#1876D2"]}
                          textInfo="Consultando Pedidos"
                        />
                      ) : (
                        "Pesquisar"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </Paper>
          </Grid>

          {/* Linha 3 - Tabela de Pedidos */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📋 Lista de Pedidos</h4>
              
              <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                {pedidos.length > 0 ? (
                  <>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Data</TableCell>
                            <TableCell>Valor</TableCell>
                            <TableCell>Cliente</TableCell>
                            <TableCell>Itens do Pedido</TableCell>
                            <TableCell>Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pedidos.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>{row.id}</TableCell>
                              <TableCell>
                                {formatDateToDDMMYYYY(row.dataCriacao)}
                              </TableCell>
                              <TableCell>{formatToBRL(row.valorTotal)}</TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  <strong>Nome:</strong> {row.cliente?.nome}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Documento:</strong> {row.cliente?.documento}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Telefone:</strong> {row.cliente?.telefone}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Email:</strong> {row.cliente?.email}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Endereço:</strong>{" "}
                                  {`${row.cliente?.cep}, ${row.cliente?.logradouro}, ${row.cliente?.numero}, ${row.cliente?.complemento}, ${row.cliente?.bairro}, ${row.cliente?.cidade} - ${row.cliente?.estado}`}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {row.itensPedido?.map((item) => (
                                  <div key={item.idProduto}>
                                    <Typography variant="body2">
                                      <strong>Produto:</strong> {item.descricao}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Quantidade:</strong> {item.quantidade}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Preço:</strong> {formatToBRL(item.preco)}
                                    </Typography>
                                  </div>
                                ))}
                                <Typography variant="body2">
                                  <strong>Forma de Pagamento:</strong>{" "}
                                  {row.formaPagamento || row.pagamentosPedido.map((p) => p.formaPagamento).join(", ")}
                                </Typography>
                                {row.quantidadeParcelas > 0 && (
                                  <Typography variant="body2">
                                    <strong>Quantidade Parcelas:</strong>{" "}
                                    {row.quantidadeParcelas}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Grid sx={{display: 'flex'}} spacing={1}>
                                  <Grid item>
                                    <Button
                                      color="primary"
                                      size="small"
                                      onClick={() => handlePrint(row)}
                                    >
                                      <PrintIcon />
                                    </Button>
                                  </Grid>
                                  <Grid item>
                                    <Button
                                      size="small"
                                      color="primary"
                                      onClick={() => handleEditPedido(row)}
                                    >
                                      <EditIcon />
                                    </Button>
                                  </Grid>
                                  <Grid item>
                                    <Button
                                      size="small"
                                      color="primary"
                                      onClick={() => handleDeleteItem(row)}
                                    >
                                      <DeleteIcon />
                                    </Button>
                                  </Grid>
                                </Grid>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <TablePagination
                      rowsPerPageOptions={[10]}
                      component="div"
                      count={pedidos.length ?? 0}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </>
                ) : (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Nenhum pedido encontrado para os filtros selecionados
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );  
}
